import { creditsRemainingPopoverOpenAtom } from '@/atoms/chatAtoms';
import { AdvancedEvidenceQueryInfoModal } from '@/components/chat/components/advanced-evidence-query-info-modal';
import {
  IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
} from '@/features/flags/legacy-flags';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemHeader,
  ItemTitle,
} from '@heidi/design-system/item';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import { TypographyCaption, TypographyPBold } from '@heidi/design-system/typography';
import {
  openTeamTrialUpgradeRequestModalAtom,
  TeamTrialUpgradeRequestButton,
  useOpenEvidenceUpgradeModal,
} from '@heidi/commerce';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import {
  ADVANCED_EVIDENCE_IN_SESSION_QUOTA,
  useEvidenceInSessionLimits,
} from '@/hooks/useEvidenceInSessionLimits';
import useReverseTrial from '@/hooks/useReverseTrial';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { trackCreditsRemainingPopoverTeamUpgradeRequestSubmit } from '@/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import type { Dispatch } from '@/store';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useSetAtom } from 'jotai';
import {
  cloneElement,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { track } from '@/lib/analytics/track';

/** Delay before opening on hover to avoid accidental triggers. */
const OPEN_DELAY_MS = 150;
/** Grace period when moving pointer from trigger to panel content. */
const CLOSE_DELAY_MS = 200;

type PopoverTriggerElementProps = {
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;
};

export interface CreditsRemainingPopoverProps {
  children: ReactNode;
}

export function CreditsRemainingPopover({ children }: CreditsRemainingPopoverProps) {
  const dispatch = useDispatch<Dispatch>();
  const { t } = useTranslations();
  const [open, setOpen] = useAtom(creditsRemainingPopoverOpenAtom);
  const {
    advancedRemaining: creditsRemaining,
    advancedUsed: creditsUsed,
    advancedResetDate,
  } = useEvidenceInSessionLimits();
  const { data: newUserTier } = useQueryUserBillingInfo((user) => user?.new_product_tier);
  const { isClinicianTrialUser, isClinicianPracticeTrialUser } = useReverseTrial();
  const { isNonAdminTeamMember, isLoadingTeamDetails } =
    useCombinedQueriesTeamGeneralInfo();
  const { openEvidenceUpgradeModal } = useOpenEvidenceUpgradeModal();
  const openTeamTrialUpgradeRequestModal = useSetAtom(
    openTeamTrialUpgradeRequestModalAtom
  );

  const [advancedEvidenceDetailsOpen, setAdvancedEvidenceDetailsOpen] =
    useState(false);

  const isHoveringRef = useRef(false);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: {
        screen_name: ScreenName.CreditsRemainingPopover,
        target_plan: targetPlan,
        ...(newUserTier !== undefined &&
          newUserTier !== null && { current_plan: newUserTier }),
      },
    });
    // Fire once per open; tier is read at impression time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clearOpenDelay = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
  }, []);

  const clearCloseDelay = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handlePointerEnter = useCallback(() => {
    clearCloseDelay();
    isHoveringRef.current = true;
    openTimeoutRef.current = setTimeout(() => {
      openTimeoutRef.current = null;
      if (isHoveringRef.current) {
        setOpen(true);
      }
    }, OPEN_DELAY_MS);
  }, [clearCloseDelay, setOpen]);

  const handlePointerLeave = useCallback(() => {
    clearOpenDelay();
    isHoveringRef.current = false;
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      if (!isHoveringRef.current) {
        setOpen(false);
      }
    }, CLOSE_DELAY_MS);
  }, [clearOpenDelay, setOpen]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        clearCloseDelay();
        setOpen(true);
        return;
      }
      if (!isHoveringRef.current) {
        setOpen(false);
      }
    },
    [clearCloseDelay, setOpen]
  );

  const isScribePlusUser = newUserTier === SubscriptionTier.PRO;
  const isPracticeUpgradeTarget =
    newUserTier === SubscriptionTier.PRO_PLUS || isClinicianPracticeTrialUser;
  const targetPlan = isPracticeUpgradeTarget
    ? SubscriptionTier.CLINICIAN_PRO_PLUS
    : SubscriptionTier.CLINICIAN_PRO;

  // Non-admin team members can't self-upgrade — they get a "Request to upgrade" CTA
  // that opens the team upgrade request modal instead of the self-serve paywall.
  // Gated on the same flags as the rest of the EVI-1474 / 90-day-trial work so the
  // popover, settings billing card, and trial recap flows roll out together.
  // Excludes CLINICIAN_PRO_PLUS (top tier) so we never present a request to a
  // *lower* tier — mirrors the CurrentPlanUsageSection guard.
  const showTeamUpgradeRequestCta =
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() &&
    !isLoadingTeamDetails &&
    isNonAdminTeamMember &&
    newUserTier !== SubscriptionTier.CLINICIAN_PRO_PLUS;

  const closePopover = useCallback(() => setOpen(false), [setOpen]);

  // Open the request modal via the atom directly so we can close the popover first;
  // passing this through `onOpenTeamRequestModal` skips the button's default atom open.
  const handleOpenTeamRequestModal = useCallback(() => {
    closePopover();
    openTeamTrialUpgradeRequestModal({
      plan: targetPlan,
      trackRequestSubmitted: trackCreditsRemainingPopoverTeamUpgradeRequestSubmit,
    });
  }, [closePopover, openTeamTrialUpgradeRequestModal, targetPlan]);

  const openAppropriateUpgradeModal = useCallback(() => {
    openEvidenceUpgradeModal({
      isScribePlusUser,
      isClinicianTrialUser,
      isPracticeUpgradeTarget,
    });
  }, [
    isClinicianTrialUser,
    isPracticeUpgradeTarget,
    isScribePlusUser,
    openEvidenceUpgradeModal,
  ]);

  const handleLearnMore = useCallback(() => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.CreditsRemainingPopover,
        action: ButtonAction.CreditsRemainingPopoverLearnMore,
        label: 'Learn more',
        target_plan: targetPlan,
        ...(newUserTier !== undefined &&
          newUserTier !== null && { current_plan: newUserTier }),
      },
    });
    closePopover();
    setAdvancedEvidenceDetailsOpen(true);
  }, [closePopover, dispatch, newUserTier, targetPlan]);

  const handleUpgrade = useCallback(() => {
    openAppropriateUpgradeModal();
    closePopover();
  }, [closePopover, openAppropriateUpgradeModal]);

  const handleViewUsage = useCallback(() => {
    closePopover();
    // Open billing in the settings modal in place, over the chat, instead of a
    // full-page nav to /settings/billing that would unmount the chat. The
    // globally-mounted `useSettingsModalUrlSync` reconciles this `?settings=`
    // change (via popstate) into an in-place modal open.
    //
    // The `settings` param and `billing` slug are inline literals, NOT imports
    // from `@/app-shell/settings/atoms/settingsModalSlugs`: this file has no
    // grandfathered edge into settings, so importing them fails the shrink-only
    // `pnpm migration:check` chat-quarantine gate (markdown-components imports
    // the same constants only because its edge is already baselined). The test
    // pins the URL against those exported constants so a slug/param rename can't
    // drift silently.
    const params = new URLSearchParams(window.location.search);
    params.set('settings', 'billing');
    // Preserve any existing hash to match the canonical `buildUrlWithSlug` in
    // `useSettingsModalUrlSync` (it forwards the hash to the modal); `params`
    // always carries `settings=billing`, so the `?` is never dangling.
    window.history.pushState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}${window.location.hash}`
    );
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [closePopover]);

  const upgradeLabel = isPracticeUpgradeTarget
    ? t('upgrade.upgradeToPractice')
    : t('upgrade.upgradeToClinician');

  let triggerChild: ReactNode = children;
  if (isValidElement(children)) {
    const child = children as ReactElement<PopoverTriggerElementProps>;
    triggerChild = cloneElement(child, {
      onMouseEnter: (event: MouseEvent) => {
        child.props.onMouseEnter?.(event);
        handlePointerEnter();
      },
      onMouseLeave: (event: MouseEvent) => {
        child.props.onMouseLeave?.(event);
        handlePointerLeave();
      },
    });
  }

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
        <PopoverTrigger asChild>{triggerChild}</PopoverTrigger>
        <PopoverContent
          align="end"
          side="top"
          sideOffset={8}
          className="w-[289px] max-w-[calc(100vw-2rem)] gap-0 p-0 py-4 shadow-xl"
          onMouseEnter={handlePointerEnter}
          onMouseLeave={handlePointerLeave}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 px-4">
              <div className="flex min-w-0 items-center gap-1">
                {isPracticeUpgradeTarget ? (
                  <TierPracticeFilled size={16} className="shrink-0 text-tier-practice-text" />
                ) : (
                  <TierProFilled size={16} className="shrink-0 text-tier-pro-text" />
                )}
                <TypographyPBold className="text-text-primary">
                  {isPracticeUpgradeTarget ? (
                    <FormattedMessage
                      id="kP8mN2vQxR"
                      defaultMessage="{count} Practice credits left"
                      description="Credits popover title for Practice tier users"
                      values={{ count: creditsRemaining }}
                    />
                  ) : (
                    <FormattedMessage
                      id="wL3hJ9fTyU"
                      defaultMessage="{count} Clinician credits left"
                      description="Credits popover title for Clinician tier users"
                      values={{ count: creditsRemaining }}
                    />
                  )}
                </TypographyPBold>
              </div>

              <TypographyCaption className="text-text-secondary">
                <FormattedMessage
                  id="bN4cR7sWmE"
                  defaultMessage="Each credit covers one advanced action, like an Evidence query, calculation or export. {learnMoreLink}"
                  description="Credits popover intro; learnMoreLink is the inline link"
                  values={{
                    learnMoreLink: (
                      <Button
                        type="button"
                        variant="link"
                        size="xxs"
                        className="inline h-auto p-0 font-normal text-inherit underline hover:text-text-link-hover"
                        onClick={handleLearnMore}
                      >
                        <FormattedMessage
                          id="TdTXXf940t"
                          defaultMessage="Learn more"
                        />
                      </Button>
                    ),
                  }}
                />
              </TypographyCaption>

              <Item size="sm" className="px-0 hover:bg-transparent">
                <ItemContent className="gap-0">
                  <ItemHeader className="items-center gap-2">
                    <ItemTitle>
                      <FormattedMessage
                        id="xY2pQ5nVkA"
                        defaultMessage="{used} of {total} used"
                        description="Credits popover usage count"
                        values={{
                          used: creditsUsed,
                          total: ADVANCED_EVIDENCE_IN_SESSION_QUOTA,
                        }}
                      />
                    </ItemTitle>
                  </ItemHeader>
                  <ItemDescription>
                    <FormattedMessage
                      id="cNh6ay1ZXf"
                      defaultMessage="Refreshes {date}"
                      values={{ date: advancedResetDate }}
                    />
                  </ItemDescription>
                </ItemContent>
                <ItemActions className="self-start">
                  <Button
                    type="button"
                    variant="link"
                    size="xxs"
                    className="h-auto p-0 text-text-secondary hover:text-text-link-hover"
                    onClick={handleViewUsage}
                  >
                    <FormattedMessage
                      id="mZ6tH1cFpB"
                      defaultMessage="View usage"
                      description="Link to settings billing from credits popover"
                    />
                  </Button>
                </ItemActions>
              </Item>
            </div>

            <div className="px-4">
              {showTeamUpgradeRequestCta ? (
                <TeamTrialUpgradeRequestButton
                  plan={targetPlan}
                  onOpenTeamRequestModal={handleOpenTeamRequestModal}
                />
              ) : (
                <Button
                  type="button"
                  variant={isPracticeUpgradeTarget ? 'pro_plus' : 'pro'}
                  size="md"
                  onClick={handleUpgrade}
                  className="w-full gap-x-1.5"
                >
                  {isPracticeUpgradeTarget ? (
                    <TierPracticeFilled className="shrink-0" />
                  ) : (
                    <TierProFilled className="shrink-0" />
                  )}
                  {upgradeLabel}
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <AdvancedEvidenceQueryInfoModal
        open={advancedEvidenceDetailsOpen}
        onOpenChange={setAdvancedEvidenceDetailsOpen}
        onUpgrade={openAppropriateUpgradeModal}
        variant={isPracticeUpgradeTarget ? 'practice' : 'clinician'}
        currentPlan={newUserTier}
      />
    </>
  );
}
